import React, { PropsWithChildren } from 'react';
import {
  AppBar,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  SvgIcon,
  Tab,
  Tabs,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { featureFlagsApiRef, useApi } from '@backstage/core-plugin-api';
import CodeIcon from '@material-ui/icons/Code';
import KeyboardIcon from '@material-ui/icons/Keyboard';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  sidebarConfig,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import MapIcon from '@material-ui/icons/Map';
import { useLocation, useNavigate } from 'react-router-dom';

type NavItem = {
  text: string;
  to: string;
  icon?: React.ElementType;
  featureFlag?: string;
};

const primaryTopNavItems: NavItem[] = [
  { text: 'Home', to: '/home' },
  { text: 'Catalog', to: '/catalog' },
  { text: 'Explore', to: '/explore' },
  { text: 'Tech Radar', to: '/tech-radar', featureFlag: 'tech-radar' },
  { text: 'Gemcheck', to: '/soundcheck', icon: TrackChangesIcon, featureFlag: 'gemcheck' },
  { text: 'Create', to: '/create' },
];

const overflowTopNavItems: NavItem[] = [
  { text: 'APIs', to: '/apis', icon: CodeIcon },
  { text: 'Tech Debt', to: '/tech-debt', icon: TrackChangesIcon, featureFlag: 'techdebt' },
];

const avatarMenuItems: NavItem[] = [
  { text: 'Settings', to: '/settings' },
  { text: 'Admin', to: '/admin', icon: KeyboardIcon },
  { text: 'Contribute', to: '/contribute', icon: CodeIcon },
];

const useTopBarStyles = makeStyles(theme => ({
  '@keyframes logoSpinBurst': {
    '0%': {
      transform: 'rotate(0deg) scale(1)',
    },
    '18%': {
      transform: 'rotate(80deg) scale(1.14)',
    },
    '72%': {
      transform: 'rotate(620deg) scale(1.14)',
    },
    '100%': {
      transform: 'rotate(720deg) scale(1)',
    },
  },
  appBar: {
    backgroundColor: '#FFFFFF',
    color: '#211710',
    borderBottom: '1px solid #EFE8E2',
    boxShadow: '0 4px 18px rgba(33,23,16,0.06)',
    zIndex: theme.zIndex.modal + 1,
  },
  toolbar: {
    minHeight: 72,
    [theme.breakpoints.down('sm')]: {
      minHeight: 64,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    '&:hover $brandIcon': {
      animation: '$logoSpinBurst 1250ms cubic-bezier(0.22, 0.95, 0.35, 1)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&:hover $brandIcon': {
        animation: 'none',
      },
    },
    '&::after': {
      display: 'none',
    }
  },
  brandIcon: {
    width: 48,
    height: 48,
    display: 'block',
    transformOrigin: '50% 50%',
    willChange: 'transform',
  },
  brandText: {
    marginLeft: theme.spacing(1),
    fontFamily: 'GothamSSmMedium',
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: 0.2,
    color: '#211710',
  },
  tabs: {
    minHeight: 72,
    marginLeft: theme.spacing(1),
  },
  tab: {
    minHeight: 72,
    minWidth: 72,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: 0,
  },
  grow: {
    flexGrow: 1,
  },
  iconButton: {
    marginLeft: theme.spacing(0.5),
    color: '#211710',
  },
  mobileLogoLink: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  drawerPaper: {
    width: 296,
  },
  toolbarOffset: theme.mixins.toolbar,
  drawerTopPadding: {
    ...theme.mixins.toolbar,
    minHeight: 64,
  },
}));

const routeMatches = (pathname: string, to: string) => {
  if (to === '/home') {
    return pathname === '/' || pathname.startsWith('/home');
  }
  return pathname === to || pathname.startsWith(`${to}/`);
};

const TopBarNav = () => {
  const classes = useTopBarStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [overflowAnchorEl, setOverflowAnchorEl] = React.useState<null | HTMLElement>(null);
  const [avatarAnchorEl, setAvatarAnchorEl] = React.useState<null | HTMLElement>(null);

  const isVisible = (item: NavItem) =>
    !item.featureFlag || featureFlagsApi.isActive(item.featureFlag);

  const visiblePrimary = primaryTopNavItems.filter(isVisible);
  const visibleOverflow = overflowTopNavItems.filter(isVisible);
  const visibleAvatarMenu = avatarMenuItems.filter(isVisible);
  const activeTab =
    visiblePrimary.find(item => routeMatches(location.pathname, item.to))?.to || false;

  const navigateAndClose = (to: string) => {
    navigate(to);
    setMobileOpen(false);
    setOverflowAnchorEl(null);
    setAvatarAnchorEl(null);
  };

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          {isMobile ? (
            <>
              <IconButton
                edge="start"
                aria-label="Open navigation"
                className={classes.iconButton}
                onClick={() => setMobileOpen(true)}
              >
                <MenuIcon />
              </IconButton>
              <Link
                to="/home"
                underline="none"
                className={classes.mobileLogoLink}
                aria-label="Home"
              >
                <img src="/helios_logo.svg" alt="Helios" className={classes.brandIcon} />
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/home"
                underline="none"
                className={classes.logoLink}
                aria-label="Home"
              >
                <img src="/helios_logo.svg" alt="Helios" className={classes.brandIcon} />
                <Typography component="span" className={classes.brandText}>
                  Helios
                </Typography>
              </Link>
              <SidebarSearchModal />
              <Tabs
                value={activeTab}
                onChange={(_, value) => navigate(value)}
                className={classes.tabs}
                indicatorColor="primary"
                textColor="primary"
                aria-label="Primary navigation"
              >
                {visiblePrimary.map(item => (
                  <Tab
                    key={item.to}
                    value={item.to}
                    label={item.text}
                    className={classes.tab}
                  />
                ))}
              </Tabs>
            </>
          )}
          <div className={classes.grow} />
          {!isMobile && visibleOverflow.length > 0 && (
            <IconButton
              aria-label="Open more navigation"
              className={classes.iconButton}
              onClick={event => setOverflowAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="Open profile actions"
            className={classes.iconButton}
            onClick={event => setAvatarAnchorEl(event.currentTarget)}
          >
            <UserSettingsSignInAvatar />
          </IconButton>
        </Toolbar>
      </AppBar>

      <div className={classes.toolbarOffset} />

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerTopPadding} />
        <List>
          {[...visiblePrimary, ...visibleOverflow].map(item => {
            const IconComponent = item.icon;
            return (
              <ListItem
                button
                key={item.to}
                selected={routeMatches(location.pathname, item.to)}
                onClick={() => navigateAndClose(item.to)}
              >
                {IconComponent ? (
                  <ListItemIcon>
                    <IconComponent />
                  </ListItemIcon>
                ) : null}
                <ListItemText primary={item.text} />
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Menu
        anchorEl={overflowAnchorEl}
        keepMounted
        open={Boolean(overflowAnchorEl)}
        onClose={() => setOverflowAnchorEl(null)}
      >
        {visibleOverflow.map(item => (
          <MenuItem key={item.to} onClick={() => navigateAndClose(item.to)}>
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={avatarAnchorEl}
        keepMounted
        open={Boolean(avatarAnchorEl)}
        onClose={() => setAvatarAnchorEl(null)}
      >
        {visibleAvatarMenu.map(item => (
          <MenuItem key={item.to} onClick={() => navigateAndClose(item.to)}>
            {item.text}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const RootNav = () => {
  return <TopBarNav />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <>
    <RootNav />
    {children}
  </>
);
